import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogBody, DialogFooter, Button, Input, MultiSelect,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { CUSTOMER_SOURCES } from '@/pages/customers/new/constants';
import { useAddCustomerContact, type AddContactPayload } from '@/api/customer-basic';
import { useProductGroupOptions } from '@/api/product-groups';

/** `contact_roles` options — the backend enum keys for a contact's role set. */
const CONTACT_ROLE_OPTIONS = [
  { value: 'customer', label: 'Customer' },
  { value: 'technical', label: 'Technical' },
  { value: 'finance', label: 'Finance' },
  { value: 'administrative', label: 'Administrative' },
  { value: 'decision-maker', label: 'Decision maker' },
];

/** Extract a human-readable message from an axios/unknown error. */
function errorMessage(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong. Please try again.';
}

interface FormState {
  name: string;
  email: string;
  phone: string;
  position: string;
  source: string;
  roles: string[];
  interests: string[];
}

const EMPTY: FormState = { name: '', email: '', phone: '', position: '', source: '', roles: ['customer'], interests: [] };

/**
 * Add Contact dialog — creates a contact via `POST /customers/:id/contacts`.
 * Required fields (name/email/phone/source) mirror the legacy contact schema;
 * roles default to `['customer']` and language to `vi`. On success it closes and
 * invalidates the contact list. Errors surface in an in-dialog Alert.
 */
export function AddContactDialog({
  customerId, open, onOpenChange,
}: { customerId: number; open: boolean; onOpenChange: (open: boolean) => void }) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const mutation = useAddCustomerContact(customerId);
  const { data: serviceOptions = [] } = useProductGroupOptions();

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const canSubmit = !!form.name.trim() && emailValid && !!form.phone.trim() && !!form.source;

  const close = (next: boolean) => {
    if (mutation.isPending) return;
    if (!next) { setForm(EMPTY); mutation.reset(); }
    onOpenChange(next);
  };

  const submit = () => {
    if (!canSubmit) return;
    const payload: AddContactPayload = {
      contact_name: form.name.trim(),
      contact_email: form.email.trim(),
      contact_phone: form.phone.trim(),
      contact_position: form.position.trim() || undefined,
      contact_source: form.source,
      contact_roles: form.roles.length ? form.roles : ['customer'],
      contact_service_interestings: form.interests,
      contact_language: 'vi',
    };
    mutation.mutate(payload, { onSuccess: () => close(false) });
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Add contact</DialogTitle>
          <DialogDescription>Add a new person to this customer's contact list.</DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {mutation.isError ? <Alert tone="danger" title="Could not add contact">{errorMessage(mutation.error)}</Alert> : null}
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            <Field label="Name" required>
              {(p) => <Input {...p} placeholder="Full name" value={form.name} onChange={(e) => set('name', e.target.value)} />}
            </Field>
            <Field label="Email" required error={form.email && !emailValid ? 'Invalid email format' : undefined}>
              {(p) => <Input {...p} type="email" placeholder="name@company.com" invalid={!!form.email && !emailValid} value={form.email} onChange={(e) => set('email', e.target.value)} />}
            </Field>
            <Field label="Phone" required>
              {(p) => <Input {...p} placeholder="Phone number" value={form.phone} onChange={(e) => set('phone', e.target.value)} />}
            </Field>
            <Field label="Position">
              {(p) => <Input {...p} placeholder="Job title" value={form.position} onChange={(e) => set('position', e.target.value)} />}
            </Field>
            <Field label="Source" required>
              {(p) => (
                <Select value={form.source} onValueChange={(v) => set('source', v)}>
                  <SelectTrigger {...p} invalid={!form.source && mutation.isError}>
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {CUSTOMER_SOURCES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Roles">
              {() => <MultiSelect placeholder="Select roles" options={CONTACT_ROLE_OPTIONS} value={form.roles} onChange={(v) => set('roles', v)} />}
            </Field>
          </div>
          <Field label="Interested services">
            {() => <MultiSelect placeholder="Select services" options={serviceOptions} value={form.interests} onChange={(v) => set('interests', v)} />}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" disabled={mutation.isPending} onClick={() => close(false)}>Cancel</Button>
          <Button variant="primary" loading={mutation.isPending} disabled={!canSubmit} onClick={submit}>Add contact</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddContactDialog;
